import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Genre } from '@/domain/clients/client';
import { CreateClientDto } from '@/domain/clients/client-schemas';
import { IvaCondition } from '@/domain/invoices/invoice';
import { DocType } from '@/domain/shared';
import { useCreateClient } from '@/hooks/api/useClients';
import { Paths } from '@/routes/paths';
import { isValidDocNumber } from '@/utils/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, CheckCircle, Circle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { InvoiceStep } from './InvoiceStep';
import { ReferenceStep } from './ReferenceStep';
import { TitularStep } from './TitularStep';

const defaultAddressValues = {
  email: '',
  street: '',
  phone: '+54343',
  city: 'Paraná',
  state: 'Entre Ríos',
  zipCode: '3100',
  country: 'Argentina',
};

const defaultContactValues = {
  name: '',
  genre: 'X',
  dateOfBirth: undefined,
  relationship: '',
  ...defaultAddressValues,
};

export const CreateClientCmp = () => {
  const createMutation = useCreateClient();
  const navigate = useNavigate();

  const baseSchema = z.object({
    userData: z.object({
      legalName: z.string().min(1, 'El nombre es requerido'),
      genre: z.enum(Object.keys(Genre)),
      docType: z.enum(Object.values(DocType)),
      docNumber: z.string().min(1, 'El número de documento es requerido'),
      invoiceLegalName: z.string().optional(),
      invoiceDocType: z.enum(Object.values(DocType) as any).optional(),
      invoiceDocNumber: z.string().optional(),
      invoiceAddress: z.string().optional(),
      ivaCondition: z.enum(Object.values(IvaCondition)),
      paidByUserId: z.string().optional(),
      dateOfBirth: z.any().optional(),
      phone: z.string().min(7, 'El número de teléfono requiere 7 caracteres'),
      email: z.string().email().optional().or(z.literal('')).or(z.undefined()),
      street: z.string().min(1, 'La Dirección es requerida'),
      city: z.string().optional(),
      state: z.string().optional(),
      zipCode: z.string().optional(),
      country: z.string().optional(),
    }),
    invoiceSameAsHolder: z.boolean().default(true),
    hasReference1: z.boolean(),
    reference1: z.any().optional(),
    hasReference2: z.boolean(),
    reference2: z.any().optional(),
  });

  const form = useForm<CreateClientDto>({
    resolver: zodResolver(baseSchema) as any,
    defaultValues: {
      userData: {
        legalName: '',
        genre: 'X',
        docType: DocType.DNI,
        docNumber: '',
        invoiceLegalName: '',
        invoiceDocType: DocType.DNI,
        invoiceDocNumber: '',
        invoiceAddress: '',
        ivaCondition: IvaCondition.CONSUMIDOR_FINAL,
        paidByUserId: '',
        dateOfBirth: undefined as any,
        ...defaultAddressValues,
      },
      invoiceSameAsHolder: true,
      hasReference1: false,
      reference1: defaultContactValues,
      hasReference2: false,
      reference2: defaultContactValues,
    },
  });

  // console.log(form.getValues())

  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    { title: 'Información del Titular', key: 'titular' },
    { title: 'Datos de Facturación', key: 'invoice_data' },
    { title: 'Contacto de Referencia 1', key: 'reference1' },
    { title: 'Contacto de Referencia 2', key: 'reference2' },
  ];

  // When the invoice step loads for the first time, ensure checkbox defaults to true
  useEffect(() => {
    if (steps[currentStep].key === 'invoice_data') {
      if (form.getValues('invoiceSameAsHolder') === undefined) {
        form.setValue('invoiceSameAsHolder', true);
      }
    }
  }, [currentStep]);

  const isStepCompleted = (step: number) => currentStep > step;
  const progress = ((currentStep + 1) / steps.length) * 100;

  const onSubmit = async (data: CreateClientDto) => {
    const formattedData = { ...data };

    if (formattedData.invoiceSameAsHolder) {
      formattedData.userData.invoiceLegalName = formattedData.userData.legalName;
      formattedData.userData.invoiceDocType = formattedData.userData.docType;
      formattedData.userData.invoiceDocNumber = formattedData.userData.docNumber;
    }

    if (!formattedData.hasReference1) {
      delete formattedData.reference1;
    }
    if (!formattedData.hasReference2) {
      delete formattedData.reference2;
    }

    await createMutation.mutateAsync(formattedData);
  };

  const nextStep = async () => {
    let fieldsToValidate: any[] = [];

    if (steps[currentStep].key === 'titular') {
      fieldsToValidate = [
        'userData.legalName',
        'userData.docType',
        'userData.docNumber',
        'userData.genre',
        'userData.phone',
        'userData.street',
        'userData.city',
        'userData.state',
        'userData.zipCode',
      ];
    } else if (steps[currentStep].key === 'invoice_data') {
      fieldsToValidate = [
        'invoiceSameAsHolder',
        'userData.ivaCondition',
      ];

      if (!form.getValues('invoiceSameAsHolder')) {
        fieldsToValidate.push(
          'userData.invoiceLegalName',
          'userData.invoiceDocType',
          'userData.invoiceDocNumber',
        );
      }
    } else if (steps[currentStep].key === 'reference1') {
      fieldsToValidate = ['hasReference1'];
      if (form.getValues('hasReference1')) {
        fieldsToValidate = [
          'hasReference1',
          'reference1.name',
          'reference1.genre',
          'reference1.relationship',
          'reference1.dateOfBirth',
          'reference1.phone',
        ];
      }
    } else if (steps[currentStep].key === 'reference2') {
      fieldsToValidate = ['hasReference2'];
      if (form.getValues('hasReference2')) {
        fieldsToValidate = [
          'hasReference2',
          'reference2.name',
          'reference2.genre',
          'reference2.relationship',
          'reference2.dateOfBirth',
          'reference2.phone',
        ];
      }
    }

    let isValid = await form.trigger(fieldsToValidate);

    // Validate titular doc number
    if (isValid && steps[currentStep].key === 'titular') {
      const docType = form.getValues('userData.docType');
      const docNumber = form.getValues('userData.docNumber');
      if (docType && docNumber && !isValidDocNumber(docType, docNumber)) {
        form.setError('userData.docNumber', {
          type: 'manual',
          message: 'El número de documento es inválido para el tipo de documento seleccionado',
        });
        isValid = false;
      }
    }

    // Validate invoice doc number when data differs from holder
    if (isValid && steps[currentStep].key === 'invoice_data' && !form.getValues('invoiceSameAsHolder')) {
      const invDocType = form.getValues('userData.invoiceDocType');
      const invDocNumber = form.getValues('userData.invoiceDocNumber');
      if (invDocType && invDocNumber && !isValidDocNumber(invDocType as DocType, invDocNumber)) {
        form.setError('userData.invoiceDocNumber', {
          type: 'manual',
          message: 'El número de documento es inválido para el tipo de documento seleccionado',
        });
        isValid = false;
      }
    }

    if (isValid && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const isPF =
    form.watch('userData.docType') === 'CUIT' &&
    form.watch('userData.docNumber').startsWith('30');

  const currentKey = steps[currentStep].key;

  const invoiceSameAsHolder = form.watch('invoiceSameAsHolder');
  const paidByUserId = form.watch('userData.paidByUserId');
  const isNextDisabled = currentStep === 1 && !invoiceSameAsHolder && !paidByUserId;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'SELECT') {
              if (currentStep < steps.length - 1) {
                e.preventDefault();
                nextStep();
              }
            }
          }
        }}
        className="space-y-4"
      >
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-primary">{steps[currentStep].title}</h3>
            <span className="text-sm text-muted-foreground font-medium">
              Paso {currentStep + 1} de {steps.length}
            </span>
          </div>

          {/* Progress */}
          <div className="space-y-4 mb-8">
            <div className="relative h-2 w-full bg-muted overflow-hidden rounded-full">
              <div
                className="h-full bg-primary transition-all duration-500 ease-in-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              {steps.map((step, index) => (
                <div key={index} className="flex items-center gap-1.5">
                  {isStepCompleted(index) ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Circle
                      className={`h-4 w-4 ${index === currentStep ? 'text-primary fill-primary/10' : ''}`}
                    />
                  )}
                  <span
                    className={`${index === currentStep ? 'font-bold text-primary' : 'hidden md:inline'}`}
                  >
                    {step.title}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Step content */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {currentKey === 'titular' && (
              <TitularStep form={form} />
            )}

            {currentKey === 'invoice_data' && (
              <InvoiceStep form={form} isPF={isPF} />
            )}

            {currentKey === 'reference1' && (
              <ReferenceStep form={form} referenceNumber={1} />
            )}

            {currentKey === 'reference2' && (
              <ReferenceStep form={form} referenceNumber={2} />
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (currentStep > 0) {
                setCurrentStep(currentStep - 1);
              } else {
                navigate({ to: `${Paths.Management.Clients}` });
              }
            }}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {currentStep === 0 ? 'Cancelar' : 'Anterior'}
          </Button>

          {currentStep < steps.length - 1 ? (
            <Button
              variant="info"
              key={`next-btn-${currentStep}`}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                nextStep();
              }}
              disabled={isNextDisabled}
            >
              Siguiente
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="success"
              key="submit-btn"
              type="submit"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creando...' : 'Crear Cliente'}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
};
